/**
 * Sprint 4I1.C — CodiceIvaPicker (autocomplete / typeahead)
 *
 * Combobox shadcn (Popover + Command/cmdk) per selezionare un codice IVA
 * da master_data_codici_iva. Retrocompatibilità totale con la versione basic:
 *   Props: value, onChange, disabled, testId, placeholder, compact
 *
 * Ricerca su: codice, descrizione, aliquota (es. "22", "I22", "esent", "intra").
 */
import React, { useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { api } from "@/lib/api";

// Cache process-wide (riutilizzata fra istanze)
let _ivaCache = null;
let _ivaPromise = null;

async function loadCodiciIva() {
  if (_ivaCache) return _ivaCache;
  if (_ivaPromise) return _ivaPromise;
  _ivaPromise = api.get("/master-data/codici-iva?attivo=true").then((r) => {
    _ivaCache = r.data || [];
    _ivaPromise = null;
    return _ivaCache;
  }).catch(() => {
    _ivaPromise = null;
    return [];
  });
  return _ivaPromise;
}

export function invalidateCodiciIvaCache() {
  _ivaCache = null;
  _ivaPromise = null;
}

/** Hook helper: ritorna lista codici IVA (cached). */
export function useCodiciIva() {
  const [list, setList] = useState([]);
  useEffect(() => {
    let m = true;
    loadCodiciIva().then((d) => { if (m) setList(d); });
    return () => { m = false; };
  }, []);
  return list;
}

/** Helper sincrono: ritorna aliquota numerica dato un codice. Usa cache. */
export function getAliquotaFromCodice(codice) {
  if (!codice || !_ivaCache) return null;
  const c = _ivaCache.find((x) => x.codice === codice);
  return c ? Number(c.aliquota) : null;
}

export default function CodiceIvaPicker({
  value, onChange, disabled, testId, placeholder = "IVA…", compact = false,
}) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    loadCodiciIva().then((d) => {
      if (mounted) {
        setList(d);
        setLoading(false);
      }
    });
    return () => { mounted = false; };
  }, []);

  const selected = useMemo(
    () => list.find((c) => c.codice === value) || null,
    [list, value],
  );

  const heightCls = compact ? "h-8 text-xs px-2" : "h-10 text-sm px-3";

  const triggerLabel = selected
    ? <span className="font-mono">{selected.codice}<span className="text-slate-500 ml-1.5">— {selected.aliquota}%</span></span>
    : <span className="text-slate-400">{loading ? "…" : placeholder}</span>;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative w-full">
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled || loading}
            data-testid={testId}
            className={cn(
              "w-full justify-between font-normal bg-white",
              heightCls,
              selected && !disabled ? "pr-12" : "",
            )}
          >
            <span className="truncate text-left flex-1">{triggerLabel}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 ml-1 shrink-0" />
          </Button>
        </PopoverTrigger>
        {selected && !disabled && (
          <button
            type="button"
            tabIndex={-1}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange(null);
            }}
            data-testid={testId ? `${testId}-clear` : undefined}
            title="Rimuovi selezione"
            className="absolute right-7 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-rose-600 rounded"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[280px]" align="start">
        <Command
          filter={(itemValue, search) => {
            // itemValue è il codice; aggiungo descrizione+aliquota tramite keywords
            const c = list.find((x) => x.codice === itemValue);
            if (!c) return 0;
            const hay = `${c.codice} ${c.aliquota}% ${c.descrizione || ""} ${c.natura_sdi || ""}`.toLowerCase();
            return hay.includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Cerca codice, descrizione, aliquota…"
                        data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList>
            <CommandEmpty>Nessun codice trovato.</CommandEmpty>
            <CommandGroup>
              {list.map((c) => (
                <CommandItem
                  key={c.codice}
                  value={c.codice}
                  onSelect={(v) => {
                    onChange(v === value ? null : v);
                    setOpen(false);
                  }}
                  data-testid={testId ? `${testId}-opt-${c.codice}` : undefined}
                >
                  <Check className={cn("mr-2 h-4 w-4",
                    value === c.codice ? "opacity-100" : "opacity-0")} />
                  <div className="flex items-baseline gap-2 flex-1 min-w-0">
                    <span className="font-mono text-sm">{c.codice}</span>
                    <span className="text-slate-600 text-xs whitespace-nowrap">{c.aliquota}%</span>
                    {c.descrizione && (
                      <span className="text-slate-400 text-xs truncate">— {c.descrizione}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
